import {
  CaseContext,
  PartyRole,
} from '../../../../../redux/slice/aiReviewSliceV2';

export type CaseContextFieldType =
  | 'longText'
  | 'singleText'
  | 'stringList'
  | 'keywordChips'
  | 'parties'
  | 'relevantTimePeriods';

export type CaseContextFieldKey = keyof CaseContext;

export type CaseContextTocSectionId =
  | 'case-overview'
  | 'review-scope'
  | 'ai-guidance'
  | 'review-guidance';

export interface CaseContextFieldConfig {
  key: CaseContextFieldKey;
  type: CaseContextFieldType;
  labelKey: string;
  /** Omit to hide the section info icon. */
  tooltipKey?: string;
  placeholderKey?: string;
  /** Optional nested label above the input (e.g. Definition) */
  subLabelKey?: string;
  required?: boolean;
  partyFieldKeys?: {
    party: string;
    role: string;
    partyDescription: string;
  };
}

export interface CaseContextTocSectionConfig {
  id: CaseContextTocSectionId;
  titleKey: string;
  fields: CaseContextFieldConfig[];
}

export const EMPTY_PARTY_ROLE: PartyRole = {
  party: '',
  role: '',
  party_description: '',
};

export const EMPTY_CASE_CONTEXT: CaseContext = {
  executive_summary: '',
  parties_and_roles: [],
  claims: [],
  defenses: [],
  disputed_facts: [],
  key_legal_and_factual_issues: [],
  primary_date_range: '',
  important_subperiods: [],
  custodians: [],
  departments: [],
  systems_and_repositories: [],
  responsiveness_standard_definition: '',
  responsive_examples: [],
  non_responsive_examples: [],
  relevancy_keywords: [],
  relevance_themes: [],
  behavioral_indicators: [],
  decision_making_indicators: [],
  explicit_exclusions: [],
  reviewer_instructions: [],
  borderline_scenarios: [],
  ambiguities_and_known_limits: [],
};

export const CASE_CONTEXT_RESPONSIVENESS_FIELD_KEYS = {
  standardDefinition: 'responsiveness_standard_definition',
  responsiveExamples: 'responsive_examples',
  nonResponsiveExamples: 'non_responsive_examples',
} as const satisfies Record<string, CaseContextFieldKey>;

export const CASE_CONTEXT_RESPONSIVENESS_SUBSECTION_KEYS: readonly CaseContextFieldKey[] =
  [
    CASE_CONTEXT_RESPONSIVENESS_FIELD_KEYS.standardDefinition,
    CASE_CONTEXT_RESPONSIVENESS_FIELD_KEYS.responsiveExamples,
    CASE_CONTEXT_RESPONSIVENESS_FIELD_KEYS.nonResponsiveExamples,
  ];

export const CASE_CONTEXT_RESPONSIVENESS_EXAMPLE_KEYS: readonly CaseContextFieldKey[] =
  [
    CASE_CONTEXT_RESPONSIVENESS_FIELD_KEYS.responsiveExamples,
    CASE_CONTEXT_RESPONSIVENESS_FIELD_KEYS.nonResponsiveExamples,
  ];

export const CASE_CONTEXT_TOOLTIP_KEY_PREFIX =
  'ai.review.protocolWizard.caseContext.tooltips';

/**
 * Field grouping matches Figma AVR-19 Case Context:
 * Case Overview → Review Scope → AI Guidance → Review Guidance
 */
export const CASE_CONTEXT_TOC_SECTIONS: CaseContextTocSectionConfig[] = [
  {
    id: 'case-overview',
    titleKey: 'ai.review.protocolWizard.caseContext.sections.caseOverview',
    fields: [
      {
        key: 'executive_summary',
        type: 'longText',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.executiveSummary',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.executiveSummary`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.executiveSummary',
        required: true,
      },
      {
        key: 'parties_and_roles',
        type: 'parties',
        labelKey: 'ai.review.protocolWizard.caseContext.fields.partiesAndRoles',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.partiesAndRoles`,
        partyFieldKeys: {
          party: 'ai.review.protocolWizard.caseContext.fields.party',
          role: 'ai.review.protocolWizard.caseContext.fields.role',
          partyDescription:
            'ai.review.protocolWizard.caseContext.fields.partyDescription',
        },
      },
      {
        key: 'claims',
        type: 'stringList',
        labelKey: 'ai.review.protocolWizard.caseContext.fields.claims',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.claims`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: 'defenses',
        type: 'stringList',
        labelKey: 'ai.review.protocolWizard.caseContext.fields.defenses',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.defenses`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: 'disputed_facts',
        type: 'stringList',
        labelKey: 'ai.review.protocolWizard.caseContext.fields.disputedFacts',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.disputedFacts`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: 'key_legal_and_factual_issues',
        type: 'stringList',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.keyLegalAndFactualIssues',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.keyLegalAndFactualIssues`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        // Composite card: Primary Date Range + Important Subperiods nest under
        // Relevant Time Periods (AVR-22183). Data still lives on CaseContext keys.
        key: 'primary_date_range',
        type: 'relevantTimePeriods',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.relevantTimePeriods',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.relevantTimePeriods`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
    ],
  },
  {
    id: 'review-scope',
    titleKey: 'ai.review.protocolWizard.caseContext.sections.reviewScope',
    fields: [
      {
        key: 'custodians',
        type: 'stringList',
        labelKey: 'ai.review.protocolWizard.caseContext.fields.custodians',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.custodians`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: 'departments',
        type: 'stringList',
        labelKey: 'ai.review.protocolWizard.caseContext.fields.departments',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.departments`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: 'systems_and_repositories',
        type: 'stringList',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.systemsAndRepositories',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.systemsAndRepositories`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: CASE_CONTEXT_RESPONSIVENESS_FIELD_KEYS.standardDefinition,
        type: 'longText',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.responsivenessStandard',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.responsivenessStandard`,
        subLabelKey: 'ai.review.protocolWizard.caseContext.fields.definition',
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.responsivenessStandard',
      },
      {
        key: CASE_CONTEXT_RESPONSIVENESS_FIELD_KEYS.responsiveExamples,
        type: 'stringList',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.responsiveExamples',
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: CASE_CONTEXT_RESPONSIVENESS_FIELD_KEYS.nonResponsiveExamples,
        type: 'stringList',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.nonResponsiveExamples',
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
    ],
  },
  {
    id: 'ai-guidance',
    titleKey: 'ai.review.protocolWizard.caseContext.sections.aiGuidance',
    fields: [
      {
        key: 'relevancy_keywords',
        type: 'keywordChips',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.relevancyKeywords',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.relevancyKeywords`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: 'relevance_themes',
        type: 'stringList',
        labelKey: 'ai.review.protocolWizard.caseContext.fields.relevanceThemes',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.relevanceThemes`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: 'behavioral_indicators',
        type: 'stringList',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.behavioralIndicators',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.behavioralIndicators`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: 'decision_making_indicators',
        type: 'stringList',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.decisionMakingIndicators',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.decisionMakingIndicators`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
    ],
  },
  {
    id: 'review-guidance',
    titleKey: 'ai.review.protocolWizard.caseContext.sections.reviewGuidance',
    fields: [
      {
        key: 'explicit_exclusions',
        type: 'stringList',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.explicitExclusions',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.explicitExclusions`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: 'reviewer_instructions',
        type: 'stringList',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.reviewerInstructions',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.reviewerInstructions`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: 'borderline_scenarios',
        type: 'stringList',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.borderlineScenarios',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.borderlineScenarios`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
      {
        key: 'ambiguities_and_known_limits',
        type: 'stringList',
        labelKey:
          'ai.review.protocolWizard.caseContext.fields.ambiguitiesAndKnownLimits',
        tooltipKey: `${CASE_CONTEXT_TOOLTIP_KEY_PREFIX}.ambiguitiesAndKnownLimits`,
        placeholderKey:
          'ai.review.protocolWizard.caseContext.placeholders.addItem',
      },
    ],
  },
];

export const getCaseContextSectionAnchorId = (
  sectionId: CaseContextTocSectionId
) => `case-context-section-${sectionId}`;
